import type { KeyboardEvent, MouseEvent } from 'react';
import { Link } from 'react-router';
import { EM_DASH } from '@viq-admin/ui';
import { useCompanyScope } from './CompanyScopePicker';
import { companyHref, conferenceHref, leadHref, userHref } from './entityHrefs';

/**
 * One link per console entity, so a name shown anywhere opens that record's
 * page in one click. Every console page that shows a company, user, rep,
 * conference or lead name renders it through one of these.
 *
 * Clicks and key presses stop propagating: these often sit inside clickable
 * table rows (DataTable's `onRowClick`), which bind both click and Enter/Space.
 * Following the link must not also fire the row's own navigation, and Enter on
 * a focused link must follow the link rather than open the row.
 *
 * Labels never fall back to a bare id (same rule as `formatEntityRef`): a
 * GUID in a cell is noise the operator cannot act on.
 */

const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();

/**
 * A tenant link. `name` is optional: without it the label comes from the
 * tenant list CompanyScopeProvider already holds. That list has active
 * tenants only, so an archived tenant with no `name` reads "Archived company".
 */
export function CompanyLink({ id, name }: { id: string | null | undefined; name?: string | null }) {
  const { companies } = useCompanyScope();
  if (!id) return <>{name || EM_DASH}</>;
  const label = name || companies.find((c) => c.id === id)?.name || 'Archived company';
  return (
    <Link to={companyHref(id)} onClick={stop} onKeyDown={stop}>
      {label}
    </Link>
  );
}

/** A user (or rep) link, by Entra object id. */
export function UserLink({ oid, name }: { oid: string | null | undefined; name?: string | null }) {
  if (!oid) return <>{name || EM_DASH}</>;
  return (
    <Link to={userHref(oid)} onClick={stop} onKeyDown={stop}>
      {name || 'User'}
    </Link>
  );
}

/** A conference link. Resolve `name` with `useConferenceNameMap` when the row only has an id. */
export function ConferenceLink({ id, name }: { id: string | null | undefined; name?: string | null }) {
  if (!id) return <>{name || EM_DASH}</>;
  return (
    <Link to={conferenceHref(id)} onClick={stop} onKeyDown={stop}>
      {name || 'Conference'}
    </Link>
  );
}

/** A lead link; `name` is the lead's preview text. */
export function LeadLink({ id, name }: { id: string; name?: string | null }) {
  return (
    <Link to={leadHref(id)} onClick={stop} onKeyDown={stop}>
      {name || '(no name)'}
    </Link>
  );
}
